import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { useClickOutsideBlock } from "../../helpers/useClickOutside";
import { LoaderSpinner } from "../LoaderSpinner";
import { HexColorPicker } from "react-colorful";

export const EditCategoryModal = ({
    isOpen,
    onClose,
    onSubmit,
    isLoading,
    category
}) => {
    const modalRef = useRef(null);
    const inputRef = useRef(null);
    const [categoryName, setCategoryName] = useState("");
    const [bgColor, setBgColor] = useState("#ffffff");
    const [textColor, setTextColor] = useState("#000000");
    const [showBgColorPicker, setShowBgColorPicker] = useState(false);
    const [showTextColorPicker, setShowTextColorPicker] = useState(false);

    useClickOutsideBlock(modalRef, onClose);

    useEffect(() => {
        if (isOpen && category) {
            setCategoryName(category.name || "");
            const colors = category.colors || {};
            setBgColor(colors.bg_color || "#ffffff");
            setTextColor(colors.text_color || "#000000");
        }
    }, [isOpen, category]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setCategoryName("");
            setBgColor("#ffffff");
            setTextColor("#000000");
            setShowBgColorPicker(false);
            setShowTextColorPicker(false);
        }
    }, [isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmedName = categoryName.trim();
        if (!trimmedName) {
            return;
        }
        await onSubmit({
            name: trimmedName,
            colors: {
                bg_color: bgColor,
                text_color: textColor
            }
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div aria-hidden="true" className="overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-40 justify-center items-center w-full md:inset-0 h-[calc(100%)] max-h-full">
            <div className="bg-gray-700 top-0 right-0 left-0 fixed z-40 justify-center items-center w-full md:inset-0 h-[calc(100%)] max-h-full opacity-30" />
            <div ref={modalRef} className="relative p-5 w-full max-w-md max-h-full bg-white border top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 rounded-md">
                <div className="flex justify-end mb-4">
                    <button 
                        onClick={onClose}
                        disabled={isLoading}
                        className="inline-flex items-center p-1 border border-transparent rounded-md shadow-sm text-white bg-primary hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>
                <h4 className="text-xl font-semibold mb-4">Edit Category</h4>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label htmlFor="category-name" className="block text-sm font-medium text-gray-700 mb-2">
                            Category Name
                        </label>
                        <input
                            ref={inputRef}
                            id="category-name"
                            type="text"
                            value={categoryName}
                            onChange={(e) => setCategoryName(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isLoading}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="Enter category name"
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Background Color
                        </label>
                        <div className="relative">
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowBgColorPicker(!showBgColorPicker);
                                        setShowTextColorPicker(false);
                                    }}
                                    className="w-16 h-10 rounded-md border-2 border-gray-300 shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    style={{ backgroundColor: bgColor }}
                                    disabled={isLoading}
                                />
                                <input
                                    type="text"
                                    value={bgColor}
                                    onChange={(e) => setBgColor(e.target.value)}
                                    disabled={isLoading}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    placeholder="#ffffff"
                                />
                            </div>
                            {showBgColorPicker && (
                                <div className="absolute z-10 mt-2 p-2 bg-white border border-gray-300 rounded-md shadow-lg">
                                    <HexColorPicker
                                        color={bgColor}
                                        onChange={setBgColor}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Text Color
                        </label>
                        <div className="relative">
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowTextColorPicker(!showTextColorPicker);
                                        setShowBgColorPicker(false);
                                    }}
                                    className="w-16 h-10 rounded-md border-2 border-gray-300 shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    style={{ backgroundColor: textColor }}
                                    disabled={isLoading}
                                />
                                <input
                                    type="text"
                                    value={textColor}
                                    onChange={(e) => setTextColor(e.target.value)}
                                    disabled={isLoading}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    placeholder="#000000"
                                />
                            </div>
                            {showTextColorPicker && (
                                <div className="absolute z-10 mt-2 p-2 bg-white border border-gray-300 rounded-md shadow-lg">
                                    <HexColorPicker
                                        color={textColor}
                                        onChange={setTextColor}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button 
                            type="button"
                            onClick={onClose}
                            disabled={isLoading}
                            className="min-w-20 inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                        >
                            {isLoading ? <LoaderSpinner width="20" height="20" border="3" colorSpinner="gray"/> : "Cancel"}
                        </button>
                        <button 
                            type="submit"
                            disabled={isLoading || !categoryName.trim()}
                            className="min-w-24 justify-center inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                        >
                            {isLoading ? <LoaderSpinner width="20" height="20" border="3" colorSpinner="gray"/> : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
